import type { EngineEvaluation, EvaluationLine } from '../api/types';

/**
 * Pure helpers for rendering engine evaluations (§6.4). No Chessground/DOM here
 * so the mapping (centipawns → win probability, score formatting) is unit-tested.
 */

/** Centipawns → white win probability, the standard logistic eval mapping. */
export function cpToWhiteFraction(cp: number): number {
  return 1 / (1 + Math.pow(10, -cp / 400));
}

/** Clamp a fraction to [0,1] and return an integer 0–100 (CSS percentage). */
export function fractionToPercent(fraction: number): number {
  return Math.round(Math.min(1, Math.max(0, fraction)) * 100);
}

/** Format a score from the side-to-move perspective: "+0.24", "-1.10", "M3". */
export function formatScore(score: EvaluationLine['score']): string {
  if (score.type === 'mate') {
    return score.value >= 0 ? `M${score.value}` : `-M${-score.value}`;
  }
  const pawns = score.value / 100;
  return pawns > 0 ? `+${pawns.toFixed(2)}` : pawns.toFixed(2);
}

/**
 * Re-express a `side_to_move` score from White's point of view, the convention an
 * eval bar is read in: positive is good for White whoever is to move.
 */
export function whitePerspectiveScore(
  score: EvaluationLine['score'],
  sideToMove: 'w' | 'b',
): EvaluationLine['score'] {
  if (sideToMove === 'w') return score;
  // `0 - value` rather than `-value` so an even score never becomes -0.
  return { type: score.type, value: 0 - score.value };
}

/**
 * Compact label for the eval bar (White's perspective): "+0.3", "-1.2", "#4",
 * "#-2". A mate-in-0 is the side to move already mated, labelled "#".
 */
export function formatBarScore(score: EvaluationLine['score']): string {
  if (score.type === 'mate') return score.value === 0 ? '#' : `#${score.value}`;
  const pawns = score.value / 100;
  const text = Math.abs(pawns) >= 10 ? Math.abs(pawns).toFixed(0) : Math.abs(pawns).toFixed(1);
  if (text === '0.0') return '0.0';
  return `${pawns > 0 ? '+' : '-'}${text}`;
}

/** The highest-ranked line (rank 1, falling back to the first). */
export function bestLine(evaluation: EngineEvaluation): EvaluationLine | null {
  return evaluation.lines.find((line) => line.rank === 1) ?? evaluation.lines[0] ?? null;
}

/**
 * White's win fraction (0–1) for the eval bar. The score's perspective is
 * `side_to_move` (§4), so a black-to-move score is inverted before drawing.
 * Mate scores pin the bar to 0 or 1.
 */
export function whiteWinFraction(
  line: EvaluationLine | null,
  sideToMove: 'w' | 'b',
): number | null {
  if (line === null) return null;
  if (line.score.type === 'mate') {
    const sideToMoveWins = line.score.value > 0;
    return sideToMove === 'w' ? (sideToMoveWins ? 1 : 0) : (sideToMoveWins ? 0 : 1);
  }
  const fraction = cpToWhiteFraction(line.score.value);
  return sideToMove === 'w' ? fraction : 1 - fraction;
}
